import { formatDistanceToNow } from "date-fns";
import { useCallback, useEffect, useId, useState } from "react";
import PageBackBar from "../components/PageBackBar";
import { useAuth } from "../features/auth/useAuth";
import {
  useApproveSelfRegistration,
  useDenySelfRegistration,
  useSelfRegistrationRequests
} from "../features/approvals/hooks";
import type { SelfRegistrationRequest } from "../features/approvals/types";
import "../App.css";

type ListFilter = "pending" | "denied" | "approved";

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 25;

/**
 * The self-registration API returns UTC timestamps without a zone and with a space
 * ("2026-05-20 19:10:49.471107"), which browsers read as local time (4-5 hours off in
 * Eastern). Normalize to strict ISO with a Z so every browser, Safari included, reads UTC.
 */
export function parseApiTimestamp(iso: string): Date {
  const s = iso.trim().replace(" ", "T").replace(/(\.\d{3})\d+/, "$1");
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  return new Date(hasZone ? s : `${s}Z`);
}

function formatRelative(iso?: string | null) {
  if (!iso) return "";
  try {
    return formatDistanceToNow(parseApiTimestamp(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function extractAxiosDetail(e: unknown): string {
  if (e && typeof e === "object" && "response" in e) {
    const d = (e as { response?: { data?: { detail?: string | string[] } } }).response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => String(x)).join(", ");
  }
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}

const filterLabel: Record<ListFilter, string> = {
  pending: "Pending",
  denied: "Denied",
  approved: "Approved"
};

const filterHeading: Record<ListFilter, string> = {
  pending: "Self-registration — pending",
  denied: "Self-registration — denied (history)",
  approved: "Self-registration — approved (history)"
};

function filterHint(filter: ListFilter, role: string | undefined): string {
  const isOwner = role === "retailer";
  const isSuper = role === "superuser";
  if (filter === "pending") {
    if (isOwner) {
      return "You only see sign-up requests for store locations you manage. Approve to create the Workvivo user (no provisioning-portal account), or deny to record a reason and remove it from the pending list.";
    }
    if (isSuper) {
      return "Superusers can approve or deny pending requests for any store slice. Decisions are credited to your account in the decision record.";
    }
    return "You can view requests for your role. Approve or deny when the actions are available.";
  }
  if (filter === "denied") {
    return isOwner
      ? "Requests you denied stay here with the reason and optional message to the applicant (saved in the database). We do not email the applicant automatically yet from this app."
      : "Denied requests and stored notes. Data lives in the database; no email is sent automatically to the applicant from this app yet.";
  }
  return isOwner
    ? "Requests you’ve approved, including when they were decided."
    : "Approved requests. Superusers can see all stores; each owner only sees their locations.";
}

function statusBadgeStyle(status: string) {
  const s = status.toLowerCase();
  if (s === "denied") {
    return { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" as const };
  }
  if (s === "approved") {
    return { background: "#f0fdf4", color: "#166534", border: "1px solid #86efac" as const };
  }
  return { background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0" as const };
}

function DenyDialog({
  open,
  requesterName,
  onCancel,
  onConfirm,
  isPending
}: {
  open: boolean;
  requesterName: string;
  onCancel: () => void;
  onConfirm: (args: { decisionReason: string; messageToRequester: string }) => void;
  isPending: boolean;
}) {
  const reasonId = useId();
  const messageId = useId();
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setMessage("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background: "rgba(15, 23, 42, 0.45)"
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={reasonId + "-label"}
        style={{
          width: "100%",
          maxWidth: "min(32rem, 100%)",
          background: "#fff",
          borderRadius: "0.85rem",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          border: "1px solid #e3e8ee",
          padding: "1.25rem 1.35rem"
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id={reasonId + "-label"} style={{ margin: "0 0 0.5rem", fontSize: "1.15rem" }}>
          Deny request
        </h2>
        <p style={{ margin: "0 0 1rem", color: "#64748b", fontSize: "0.9rem" }}>
          {requesterName.trim() || "This applicant"} — this flow is for internal provisioning only. Use the first
          field for a short, factual reason your team can see in the request history (for example, wrong role, wrong
          or incomplete work email, or the person is not in scope for this tool). The second field is optional wording
          you could reuse if we email the person later; nothing is sent from this app automatically yet.
        </p>
        <label htmlFor={reasonId} style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", color: "#0f172a" }}>
          Reason (saved on the request)
        </label>
        <textarea
          id={reasonId}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Submitted the wrong role; or work email is wrong, incomplete, or not allowed for this internal tool"
          style={{
            width: "100%",
            marginTop: "0.35rem",
            marginBottom: "0.9rem",
            padding: "0.55rem 0.65rem",
            borderRadius: "0.5rem",
            border: "1px solid #cbd5e1",
            fontSize: "0.9rem",
            fontFamily: "inherit",
            resize: "vertical",
            minHeight: "4.5rem"
          }}
        />
        <label
          htmlFor={messageId}
          style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", color: "#0f172a" }}
        >
          Message to the applicant (optional, stored for your records)
        </label>
        <textarea
          id={messageId}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          placeholder="e.g. Please go back and use your correct work email, or choose the role that matches your current position (e.g. store employee)."
          style={{
            width: "100%",
            marginTop: "0.35rem",
            marginBottom: "1.1rem",
            padding: "0.55rem 0.65rem",
            borderRadius: "0.5rem",
            border: "1px solid #cbd5e1",
            fontSize: "0.9rem",
            fontFamily: "inherit",
            resize: "vertical"
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            style={{
              padding: "0.5rem 0.9rem",
              borderRadius: "0.5rem",
              border: "1px solid #cbd5e1",
              background: "#f8fafc",
              color: "#0f172a",
              fontWeight: 600,
              cursor: isPending ? "not-allowed" : "pointer"
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm({
                decisionReason: reason.trim(),
                messageToRequester: message.trim()
              })
            }
            disabled={isPending}
            style={{
              padding: "0.5rem 0.9rem",
              borderRadius: "0.5rem",
              border: "1px solid #b91c1c",
              background: "#ef4444",
              color: "#fff",
              fontWeight: 600,
              cursor: isPending ? "not-allowed" : "pointer"
            }}
          >
            {isPending ? "Denying…" : "Confirm deny"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OutcomeBlock({ req }: { req: SelfRegistrationRequest }) {
  if (req.status === "pending") return null;
  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.75rem 0.9rem",
        background: "#f8fafc",
        borderRadius: "0.5rem",
        border: "1px solid #e3e8ee",
        fontSize: "0.88rem",
        lineHeight: 1.5,
        color: "#334155"
      }}
    >
      <div style={{ fontWeight: 650, color: "#0f172a", marginBottom: "0.35rem" }}>Decision record</div>
      {req.decision_source ? (
        <div style={{ marginBottom: "0.35rem" }}>
          <span style={{ color: "#64748b" }}>How decided: </span>
          {req.decision_source === "email"
            ? "Email (secure link)"
            : req.decision_source === "portal"
              ? "In the app"
              : req.decision_source}
        </div>
      ) : null}
      {req.decided_at ? (
        <div style={{ marginBottom: "0.35rem" }}>Decided {formatRelative(req.decided_at)}</div>
      ) : null}
      {req.decided_by_name || req.decided_by_email ? (
        <div style={{ marginBottom: "0.35rem" }}>
          <span style={{ color: "#64748b" }}>Approver: </span>
          {(req.decided_by_name || "").trim() || "—"}
          {req.decided_by_role ? (
            <span style={{ color: "#64748b" }}>
              {" "}
              ({req.decided_by_role.replaceAll("_", " ")})
            </span>
          ) : null}
          {req.decided_by_email ? <div style={{ color: "#475569" }}>{req.decided_by_email}</div> : null}
        </div>
      ) : null}
      {req.status === "denied" && req.decision_reason ? (
        <div>
          <span style={{ color: "#64748b" }}>Reason: </span>
          {req.decision_reason}
        </div>
      ) : null}
      {req.status === "denied" && req.message_to_requester ? (
        <div style={{ marginTop: "0.35rem" }}>
          <span style={{ color: "#64748b" }}>Message to applicant: </span>
          {req.message_to_requester}
        </div>
      ) : null}
      {req.status === "approved" && !req.decision_reason && !req.message_to_requester ? (
        <div style={{ color: "#64748b" }}>No extra notes on file.</div>
      ) : null}
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const [listFilter, setListFilter] = useState<ListFilter>("pending");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const requestsQuery = useSelfRegistrationRequests(listFilter, pageSize, page);
  const approveMut = useApproveSelfRegistration();
  const denyMut = useDenySelfRegistration();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [denyFor, setDenyFor] = useState<SelfRegistrationRequest | null>(null);
  const canView = user && (user.role === "superuser" || user.role === "retailer");
  const canAct = user?.role === "retailer" || user?.role === "superuser";

  const closeDeny = useCallback(() => setDenyFor(null), []);

  useEffect(() => {
    setPage(0);
  }, [listFilter]);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  useEffect(() => {
    const d = requestsQuery.data;
    if (requestsQuery.isFetching || !d) return;
    if (d.total > 0 && d.items.length === 0 && d.offset >= d.total) {
      setPage(0);
    }
  }, [requestsQuery.data, requestsQuery.isFetching]);

  if (!user || !canView) {
    return (
      <div style={{ color: "#b91c1c" }}>
        You don’t have access to view approvals.
      </div>
    );
  }

  const emptyMessage =
    listFilter === "pending"
      ? "No pending requests right now."
      : listFilter === "denied"
        ? "No denied requests in this list."
        : "No approved requests in this list.";

  const listPage = requestsQuery.data;
  const items = listPage?.items ?? [];
  const total = listPage?.total ?? 0;
  const rangeStart = total === 0 ? 0 : (listPage?.offset ?? 0) + 1;
  const rangeEnd = total === 0 ? 0 : (listPage?.offset ?? 0) + items.length;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
  const canPrev = page > 0;
  const canNext = total > 0 && (page + 1) * pageSize < total;

  return (
    <div className="approvals-page">
      <PageBackBar />
      <header>
        <h1 className="approvals-page-title">Review requests</h1>
        <p className="approvals-page-sub">
          <strong style={{ fontWeight: 700, color: "#334155" }}>{filterHeading[listFilter]}</strong>
          {" — "}
          {filterHint(listFilter, user.role)}
        </p>
      </header>

      <div className="approvals-tab-strip" role="tablist" aria-label="Request status">
        {(["pending", "denied", "approved"] as const).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={listFilter === f}
            data-active={listFilter === f}
            onClick={() => {
              setListFilter(f);
              setErrorMessage(null);
            }}
          >
            {filterLabel[f]}
            {listFilter === f && requestsQuery.data ? ` (${requestsQuery.data.total})` : ""}
          </button>
        ))}
      </div>

      {errorMessage ? (
        <div
          style={{
            padding: "0.65rem 0.9rem",
            borderRadius: "0.5rem",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b"
          }}
        >
          {errorMessage}
        </div>
      ) : null}

      {requestsQuery.isLoading ? (
        <div style={{ color: "#94a3b8" }}>Loading…</div>
      ) : requestsQuery.isError ? (
        <div style={{ color: "#b91c1c" }}>Failed to load requests.</div>
      ) : total === 0 ? (
        <div style={{ color: "#64748b" }}>{emptyMessage}</div>
      ) : (
        <>
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {items.map((req) => (
            <div key={req.id} className="approvals-request-card">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  marginBottom: "0.5rem"
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    padding: "0.2rem 0.5rem",
                    borderRadius: "0.4rem",
                    ...statusBadgeStyle(req.status)
                  }}
                >
                  {req.status}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                <div style={{ minWidth: "240px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    {req.requester_first_name} {req.requester_last_name}
                  </div>
                  <div style={{ color: "#334155" }}>{req.requester_email}</div>
                  {req.catalog_group || req.catalog_concept || req.employee_type ? (
                    <div style={{ marginTop: "0.35rem", color: "#475569", fontSize: "0.85rem" }}>
                      {req.catalog_group ? (
                        <div>
                          Group: <strong>{req.catalog_group}</strong>
                        </div>
                      ) : null}
                      {req.catalog_concept ? (
                        <div>
                          Concept: <strong>{req.catalog_concept}</strong>
                        </div>
                      ) : null}
                      {req.employee_type ? (
                        <div>
                          Employee type:{" "}
                          <strong>
                            {req.employee_type === "salaried_manager"
                              ? "Salaried Manager"
                              : req.employee_type === "hourly"
                                ? "Hourly"
                                : req.employee_type}
                          </strong>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {req.job_title || req.department || req.locations || req.region || req.cost_centre ? (
                    <div
                      style={{
                        marginTop: "0.5rem",
                        color: "#475569",
                        fontSize: "0.85rem",
                        lineHeight: 1.45
                      }}
                    >
                      {req.job_title ? (
                        <div>
                          Role: <strong>{req.job_title}</strong>
                        </div>
                      ) : null}
                      {req.department ? (
                        <div>
                          Department: <strong>{req.department}</strong>
                        </div>
                      ) : null}
                      {req.locations ? (
                        <div>
                          Location: <strong>{req.locations}</strong>
                        </div>
                      ) : null}
                      {req.region ? <div>Region: {req.region}</div> : null}
                      {req.cost_centre ? <div>Cost centre: {req.cost_centre}</div> : null}
                    </div>
                  ) : null}
                </div>

                <div style={{ minWidth: "260px" }}>
                  <div style={{ fontWeight: 650, color: "#0f172a" }}>{req.store_name ?? "Unknown location"}</div>
                  <div style={{ color: "#64748b", fontSize: "0.9rem" }}>Store ID: {req.store_id}</div>
                  <div style={{ color: "#64748b", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                    Submitted {formatRelative(req.created_at)}
                  </div>
                </div>

                <div style={{ minWidth: "260px" }}>
                  <div style={{ fontWeight: 650, color: "#0f172a" }}>Location admin contact</div>
                  {req.location_admin_email ? (
                    <>
                      <div style={{ color: "#334155" }}>{req.location_admin_name || "—"}</div>
                      <div style={{ color: "#334155" }}>{req.location_admin_email}</div>
                    </>
                  ) : (
                    <div style={{ color: "#94a3b8" }}>No admin mapped for this store.</div>
                  )}
                </div>
              </div>

              <OutcomeBlock req={req} />

              {listFilter === "pending" && canAct ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginTop: "0.9rem",
                    paddingTop: "0.75rem",
                    borderTop: "1px solid #e3e8ee"
                  }}
                >
                  <button
                    type="button"
                    className="approvals-btn-approve"
                    disabled={approveMut.isPending || denyMut.isPending}
                    onClick={async () => {
                      setErrorMessage(null);
                      try {
                        await approveMut.mutateAsync(req.id);
                      } catch (e) {
                        setErrorMessage(extractAxiosDetail(e) || "Approve failed. Check Workvivo and try again.");
                      }
                    }}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="approvals-btn-deny"
                    disabled={approveMut.isPending || denyMut.isPending}
                    onClick={() => {
                      setErrorMessage(null);
                      setDenyFor(req);
                    }}
                  >
                    Deny
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="approvals-footer-bar">
          <span>
            Showing <strong style={{ color: "#0f172a" }}>{rangeStart}</strong>–
            <strong style={{ color: "#0f172a" }}>{rangeEnd}</strong> of{" "}
            <strong style={{ color: "#0f172a" }}>{total}</strong>
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontWeight: 500 }}>
              Per page
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{
                  padding: "0.35rem 0.5rem",
                  borderRadius: "0.4rem",
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  fontSize: "0.9rem"
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!canPrev}
                style={{
                  padding: "0.35rem 0.65rem",
                  borderRadius: "0.4rem",
                  border: "1px solid #cbd5e1",
                  background: canPrev ? "#fff" : "#f1f5f9",
                  color: canPrev ? "#0f172a" : "#94a3b8",
                  fontWeight: 600,
                  cursor: canPrev ? "pointer" : "not-allowed"
                }}
              >
                Previous
              </button>
              <span style={{ padding: "0 0.25rem" }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={!canNext}
                style={{
                  padding: "0.35rem 0.65rem",
                  borderRadius: "0.4rem",
                  border: "1px solid #cbd5e1",
                  background: canNext ? "#fff" : "#f1f5f9",
                  color: canNext ? "#0f172a" : "#94a3b8",
                  fontWeight: 600,
                  cursor: canNext ? "pointer" : "not-allowed"
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      <DenyDialog
        open={denyFor != null}
        requesterName={
          denyFor ? `${denyFor.requester_first_name} ${denyFor.requester_last_name}`.trim() : ""
        }
        isPending={denyMut.isPending}
        onCancel={closeDeny}
        onConfirm={async ({ decisionReason, messageToRequester }) => {
          if (!denyFor) return;
          setErrorMessage(null);
          try {
            await denyMut.mutateAsync({
              requestId: denyFor.id,
              decision_reason: decisionReason || undefined,
              message_to_requester: messageToRequester || undefined
            });
            setDenyFor(null);
          } catch (e) {
            setErrorMessage(extractAxiosDetail(e) || "Deny failed.");
          }
        }}
      />
    </div>
  );
}
